import { isDefined } from '@sgme/fp';
import { Button, HStack, Spacer } from '@sgme/ui';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ErrorModal } from '@/App/tiles/shared/ErrorModal.tsx';
import { SgIcon } from '@/components/sg-design-system/components/display/SgIcon';
import { isNotDefined } from '@/components/sg-design-system/utils';
import { UsertypeGuard } from '@/components/UsertypeGuard.tsx';
import { selectBulkTrade } from '@/store/bulkTrade/selectors.ts';
import { useAppSelector } from '@/store/hooks.ts';

export type BulkErrorProps = {
  tabId: string;
};

export const BulkError = (props: BulkErrorProps) => {
  const { tabId } = props;

  const error = useAppSelector((state) => {
    const bulkTrade = selectBulkTrade(state, tabId);

    if (bulkTrade?.step === 'executed' && bulkTrade.status === 'rejected') {
      return {
        code: bulkTrade.errorCode,
        message: bulkTrade.reason,
        multipassErrorCode: bulkTrade.multipassErrorCode,
        sessionId: bulkTrade.sessionId,
      };
    }

    if (isNotDefined(bulkTrade?.rfsRequest) || bulkTrade.rfsRequest.status !== 'error') {
      return undefined;
    }

    return {
      code: bulkTrade.rfsRequest.code,
      message: bulkTrade.rfsRequest.message,
    };
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenViewSgLog = () => {
    setIsModalOpen(true);
  };

  if (isNotDefined(error)) {
    return undefined;
  }

  return (
    <HStack gap="16px" className="bg-danger-subtle text-danger p-16px" data-e2e="bulk-error">
      <SgIcon icon="info_outline" />
      <div>
        Error: <span data-e2e="bulk-error-message">{error.message}</span> (
        <span data-e2e="bulk-error-code">{error.code}</span>)
      </div>
      <Spacer />
      {isDefined(error.code) && (
        <UsertypeGuard allow={['Internal Sales', 'Internal Trader']}>
          <Button purpose="primary" color="danger" onClick={handleOpenViewSgLog} data-e2e="show-error-detail-btn">
            <FormattedMessage id="growl.error.viewLog" />
          </Button>
        </UsertypeGuard>
      )}
      <ErrorModal
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        multipassErrorCode={error.multipassErrorCode}
        errorCode={error.code}
        quoteId={error.sessionId}
        errorDescription={error.message}
      />
    </HStack>
  );
};
